import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useParams } from 'react-router-dom';
import Viewer from 'react-viewer';
import { ImageDecorator } from 'react-viewer/lib/ViewerProps';
import { useRecoilValue } from 'recoil';
import { ActivitySessionCard } from 'src/components/activityv3/ActivitySessionCard';
import { BackButton, Blank, Divider, TopNavbar } from 'src/components/common';
import { BadgeV2 } from 'src/components/common/BadgeV2';
import { ButtonV2 } from 'src/components/common/ButtonV2';
import { Icon } from 'src/components/common/icons';
import { TextareaV2 } from 'src/components/common/TextareaV2';
import { Typography } from 'src/components/common/Typography';
import SVGIcon from 'src/components/icon/SVGIcon';
import { Constants } from 'src/constants';
import { ACTIVITYV3_TYPE_KOR } from 'src/constants/activityv3.enum';
import { useStudentActivityV3Detail } from 'src/container/student-activityv3';
import { useStudentActivityV3FindByStudent, useStudentActivityV3SaveStudentText } from 'src/generated/endpoint';
import { ResponseActivityV3SessionByStudentDto } from 'src/generated/model';
import { meState } from 'src/store';
import { isEnded } from 'src/util/activityv3';
import { getFileNameFromUrl, isPdfFile } from 'src/util/file';

export function ActivitySessionPage() {
  const { id } = useParams<{ id: string }>();
  const meRecoil = useRecoilValue(meState);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: activityv3, refetch, isLoading } = useStudentActivityV3Detail(Number(id));
  const { data: studentActivityV3, isLoading: isStudentActivityV3Loading } = useStudentActivityV3FindByStudent({
    activityv3Id: Number(id),
  });
  const [studentTextOpen, setStudentTextOpen] = useState(false);
  const [editorText, setEditorText] = useState<string>('');
  const [editMode, setEditMode] = useState(false);
  const [hasImagesModalOpen, setImagesModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const { mutate: updateStudentText } = useStudentActivityV3SaveStudentText({
    mutation: {
      onSuccess: () => {
        refetch();
        setStudentTextOpen(false);
        setEditMode(false);
        wrapperRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
  });

  const viewerImages: ImageDecorator[] = [];
  if (activityv3?.images) {
    for (const image of activityv3.images) {
      if (isPdfFile(image) == false) {
        viewerImages.push({
          src: `${Constants.imageUrl}${image}`,
        });
      }
    }
  }

  const handleSaveStudentText = () => {
    updateStudentText({
      data: {
        studentText: editorText,
      },
      params: {
        activityv3Id: Number(id),
      },
    });
  };

  useEffect(() => {
    if (studentTextOpen) {
      wrapperRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      setEditorText(studentActivityV3?.studentText || '');
      setEditMode(false);
    }
  }, [studentTextOpen]);

  if (!activityv3 || isStudentActivityV3Loading || isLoading) return <Blank />;

  if (studentTextOpen) {
    const getButtonText = () => {
      if (studentActivityV3?.studentText) {
        return editMode ? '제출하기' : '수정하기';
      }
      return '제출하기';
    };

    const handleButtonClick = () => {
      if (editMode) {
        handleSaveStudentText();
      }
      setEditMode(!editMode);
    };
    return (
      <div className="flex h-full w-full flex-col gap-4 overflow-y-auto" ref={wrapperRef}>
        <header className="flex flex-row items-center justify-between gap-2 py-3 pl-5 pr-3">
          <Typography variant="title2" className="font-semibold">
            성찰일지 작성
          </Typography>
          <SVGIcon.Close size={24} weight="bold" color="gray700" onClick={() => setStudentTextOpen(false)} />
        </header>
        <div className="flex flex-col gap-4 px-5">
          {activityv3.explainText && (
            <div className="flex flex-col gap-2 rounded-xl bg-primary-gray-50 p-4">
              <BadgeV2 type="solid_regular" color="orange" size={20}>
                성찰일지 예시
              </BadgeV2>
              <Typography variant="body2" className="font-medium">
                {activityv3.explainText}
              </Typography>
            </div>
          )}
          <TextareaV2
            className="h-[480px]"
            placeholder={activityv3.exampleText || '성찰일지를 작성해주세요.'}
            value={editorText}
            onChange={(e) => setEditorText(e.target.value)}
            showContentCount={true}
            disabled={!editMode && !!studentActivityV3?.studentText}
          />
        </div>
        <div className="border-t border-t-primary-gray-100 px-5 pb-10 pt-5">
          <ButtonV2 variant="solid" color="orange800" size={48} className="h-14 w-full" onClick={handleButtonClick}>
            {getButtonText()}
          </ButtonV2>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto" ref={wrapperRef}>
      <TopNavbar
        left={<BackButton />}
        leftFlex="flex-none"
        rightFlex="flex-none"
        borderless
        right={<Icon.Refresh className="h-8 w-8" onClick={() => window.location.reload()} />}
      />
      <div className="flex flex-col gap-6 pt-4">
        <header className="flex flex-col gap-4 px-4">
          <div className="flex flex-col gap-1">
            <Typography variant="body3" className="flex flex-row items-center gap-1 text-primary-gray-700">
              {ACTIVITYV3_TYPE_KOR[activityv3.type]}
              <SVGIcon.Bullet color="gray400" size={20} />
              {activityv3.subject}
            </Typography>
            <Typography variant="title1" className="font-bold">
              {activityv3.title}
            </Typography>
            {activityv3.description && (
              <Typography variant="body1" className="pt-1">
                {activityv3.description}
              </Typography>
            )}
          </div>
          {(activityv3.startDate || activityv3.endDate) && (
            <div className="flex flex-row items-center justify-between gap-1 rounded-xl bg-primary-gray-50 px-4 py-3">
              <Typography variant="body2" className="text-primary-gray-600">
                활동기간
              </Typography>
              <Typography variant="body2" className="flex flex-row items-center gap-1 font-medium">
                {activityv3.startDate && format(new Date(activityv3.startDate), 'yyyy.MM.dd')}&nbsp;~&nbsp;
                {activityv3.endDate && format(new Date(activityv3.endDate), 'yyyy.MM.dd')}
              </Typography>
            </div>
          )}

          {/* 첨부파일 영역 */}
          {(activityv3.images.length > 0 || activityv3.files.length > 0) && (
            <div className="flex flex-col gap-3">
              {activityv3.images && activityv3.images.length > 0 && (
                <div className="flex w-full overflow-x-auto">
                  <div className="flex flex-row gap-2">
                    {activityv3.images.map((image: string, i: number) => (
                      <div
                        key={i}
                        className="h-20 w-20 flex-shrink-0 cursor-pointer rounded-lg"
                        onClick={() => {
                          setActiveIndex(i);
                          setImagesModalOpen(true);
                        }}
                      >
                        <LazyLoadImage
                          src={`${Constants.imageUrl}${image}`}
                          alt=""
                          loading="lazy"
                          className="h-full w-full rounded-lg object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activityv3.files && activityv3.files.length > 0 && (
                <div className="flex w-full flex-wrap gap-2">
                  {activityv3.files.map((fileUrl: string, index: number) => (
                    <a
                      href={`${Constants.imageUrl}${fileUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      download={getFileNameFromUrl(fileUrl)}
                      key={index}
                      className="flex h-10 w-max items-center gap-2 rounded-lg border border-primary-gray-200 bg-white px-3 py-2"
                    >
                      <SVGIcon.Link size={16} weight="bold" color="gray700" />
                      <Typography variant="body2" className="line-clamp-1 text-primary-gray-700">
                        {getFileNameFromUrl(fileUrl)}
                      </Typography>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </header>
        {activityv3.activitySessions && activityv3.activitySessions?.length > 0 && (
          <section className="flex flex-col gap-2 px-4">
            <Typography variant="title2" className="font-semibold">
              활동 차시
            </Typography>
            <div className="flex flex-col gap-2">
              {activityv3.activitySessions?.map((session, index) => {
                const isSubmitted = session.studentActivitySessions.some(
                  (studentSession) => studentSession.userId === meRecoil?.id && studentSession.isSubmitted,
                );

                return (
                  <ActivitySessionCard
                    key={index}
                    isEnded={isEnded(session.endDate)}
                    activitySession={session as unknown as ResponseActivityV3SessionByStudentDto}
                    isSubmitted={isSubmitted}
                    id={activityv3.id}
                    type="detail"
                  />
                );
              })}
            </div>
          </section>
        )}
        {activityv3.hasStudentText && (
          <section className="flex flex-col gap-10 py-4">
            <Divider className="h-2.5 bg-primary-gray-100" />
            <div className="flex flex-col gap-4 px-4">
              <span className="flex flex-col gap-2">
                <div className="flex flex-row items-center justify-between">
                  <Typography variant="title2" className="font-semibold">
                    학생 성찰일지
                  </Typography>
                  {!studentActivityV3?.studentText ? (
                    <BadgeV2 type="line" color="red" size={20}>
                      미제출
                    </BadgeV2>
                  ) : (
                    <BadgeV2 type="line" color="green" size={20}>
                      제출완료
                    </BadgeV2>
                  )}
                </div>
                <Typography variant="body3" className="text-primary-gray-700">
                  활동의 차시 참여 후, 생기부 작성을 위해 예시를 참고하여
                  <br />
                  학생 성찰일지를 작성해주세요.
                </Typography>
              </span>
              <div className="flex flex-row items-center justify-between gap-1 rounded-xl bg-primary-gray-50 px-4 py-3">
                <Typography variant="body2" className="text-primary-gray-600">
                  마감기한
                </Typography>
                {activityv3.studentTextEndDate && (
                  <Typography variant="body2" className="flex flex-row items-center gap-1 font-medium">
                    ~&nbsp;{format(new Date(activityv3.studentTextEndDate), 'yyyy.MM.dd HH:mm')}&nbsp;까지
                  </Typography>
                )}
              </div>
              <ButtonV2
                variant="outline"
                color="gray400"
                size={40}
                onClick={() => setStudentTextOpen(true)}
                disabled={
                  activityv3.studentTextEndDate
                    ? !activityv3.isLateSubmissionAllowed && new Date() > new Date(activityv3.studentTextEndDate)
                    : false
                }
              >
                성찰일지 작성하기
              </ButtonV2>
            </div>
          </section>
        )}
        <div className="absolute">
          <Viewer
            visible={hasImagesModalOpen}
            rotatable
            noImgDetails
            scalable={false}
            images={viewerImages}
            onChange={(activeImage, index) => setActiveIndex(index)}
            onClose={() => setImagesModalOpen(false)}
            activeIndex={activeIndex}
          />
        </div>
      </div>
    </div>
  );
}
